/**
 * EEOC Test Welcome Page
 * Public page for recruiters to access their assessment via unique link
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, BookOpen, ClipboardCheck, AlertTriangle, 
  User, Mail, ChevronRight, Mic, HelpCircle, MessageSquare,
  Loader2
} from 'lucide-react';

export default function EEOCTestWelcome() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTestData();
  }, [linkId]);

  const fetchTestData = async () => {
    try {
      const response = await api.get(`/eeoc/test/${linkId}`);
      setTestData(response.data);
    } catch (err) {
      setError('Test not found or link has expired');
      toast.error('Invalid test link');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Invalid Link</h2>
            <p className="text-slate-600 dark:text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (testData?.test_status === 'completed') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <ClipboardCheck className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Test Completed</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">You have already completed this assessment.</p>
            <Button 
              onClick={() => navigate(`/eeoc/test/${linkId}/results`)}
              className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900"
            >
              View Results
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="eeoc-welcome">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 dark:bg-slate-100 rounded-md flex items-center justify-center">
              <Shield className="w-5 h-5 text-white dark:text-slate-900" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">CallSense</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">EEOC Compliance Assessment</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Welcome Card */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl">Welcome to Your EEOC Assessment</CardTitle>
            <CardDescription className="text-base mt-2">
              This assessment evaluates your ability to conduct legally compliant interviews 
              while avoiding questions that could be considered discriminatory.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-md">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Employee</p>
                  <p className="font-medium text-slate-900 dark:text-white" data-testid="employee-name">
                    {testData?.user_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Email</p>
                  <p className="font-medium text-slate-900 dark:text-white" data-testid="employee-email">
                    {testData?.user_email}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* EEOC Reference Material */}
        <Card className="mb-6 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20 hover:border-blue-300 transition-colors cursor-pointer group"
              onClick={() => navigate(`/eeoc/test/${linkId}/guide`)}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-700 transition-colors">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    EEOC Reference Material
                  </h3>
                  <Badge className="bg-blue-600 text-white text-xs">Recommended First</Badge>
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">
                  Comprehensive guide covering US EEOC laws, UK Equality Act, protected categories, 
                  do's and don'ts, common scenarios, and redirection techniques.
                </p>
                <Button 
                  data-testid="open-guide-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/eeoc/test/${linkId}/guide`);
                  }}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Read EEOC Guide
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scenario Helper */}
        <Card className="mb-6 border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/20 hover:border-emerald-300 transition-colors cursor-pointer group"
              onClick={() => navigate(`/eeoc/test/${linkId}/scenarios`)}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-700 transition-colors">
                <HelpCircle className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Scenario Helper
                  </h3>
                  <Badge className="bg-emerald-600 text-white text-xs">Ask Questions</Badge>
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">
                  Unsure how to handle a specific situation? Describe your scenario and get 
                  <strong> instant AI-powered guidance</strong> on how to respond compliantly.
                </p>
                <Button 
                  data-testid="open-scenario-helper-btn"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/eeoc/test/${linkId}/scenarios`);
                  }}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Ask About a Scenario
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Options */}
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Assessment Options</h3>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Mock Test Card */}
          <Card className="hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-amber-50 dark:bg-amber-900/30 rounded-md flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                Mock Practice Test
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
                Familiarize yourself with the voice-based assessment format. Practice with 2-3 scenarios 
                and receive immediate feedback hints. This does not affect your final score.
              </p>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge variant="outline" className="text-xs">2-3 Scenarios</Badge>
                <Badge variant="outline" className="text-xs">~10 min</Badge>
                <Badge variant="outline" className="text-xs flex items-center gap-1">
                  <Mic className="w-3 h-3" /> Voice-Based
                </Badge>
                <Badge variant="outline" className="bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-700 text-xs">
                  Practice Mode
                </Badge>
              </div>
              <Button 
                data-testid="start-mock-btn"
                onClick={() => navigate(`/eeoc/test/${linkId}/mock`)}
                variant="outline"
                className="w-full"
              >
                Start Practice Test
              </Button>
            </CardContent>
          </Card>

          {/* Final Assessment Card */}
          <Card className="hover:border-slate-900 dark:hover:border-slate-100 transition-colors">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-md flex items-center justify-center mb-4">
                <ClipboardCheck className="w-6 h-6 text-slate-700 dark:text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                Final EEOC Assessment
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
                Complete the full assessment with 5+ interview scenarios via voice conversation. 
                Your responses will be evaluated for EEOC compliance and you'll receive detailed feedback.
              </p>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge variant="outline" className="text-xs">6+ Exchanges</Badge>
                <Badge variant="outline" className="text-xs">~20-30 min</Badge>
                <Badge variant="outline" className="text-xs flex items-center gap-1">
                  <Mic className="w-3 h-3" /> Voice-Based
                </Badge>
                <Badge variant="outline" className="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs">
                  Scored
                </Badge>
              </div>
              <Button 
                data-testid="start-assessment-btn"
                onClick={() => navigate(`/eeoc/test/${linkId}/assessment`)}
                className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900"
              >
                Start Final Assessment
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Info Notice */}
        <div className="mt-8 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-300 mb-1">Important Notice</h4>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                This assessment uses <strong>voice-based interaction</strong>. You will be speaking with an 
                AI candidate and your responses will be recorded and evaluated. Ensure you are in 
                a quiet environment with a working microphone.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 mt-auto">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
            This is an internal compliance training tool. Not for legal advice. For HR use only.
          </p>
        </div>
      </footer>
    </div>
  );
}
